import React, { useState, useRef, useCallback } from 'react';
import type {
  Category,
  CategoryItem,
  ScoreWidget,
  CategoriesManagerLabels,
} from '../../shared/types/categories';

export interface CategoriesManagerProps {
  categories: Category[];
  scoreWidgets: ScoreWidget[];
  newCategoryUrl: string;
  newScoreWidgetUrl: string;
  backUrl: string;
  onReorderCategories: (order: string[]) => void;
  onReorderCategoryItems: (categoryId: string, order: string[]) => void;
  onReorderFieldSetFields: (fieldSetId: string, order: string[]) => void;
  onNavigate?: (url: string) => void;
  labels: CategoriesManagerLabels;
}

export function CategoriesManager({
  categories,
  scoreWidgets,
  newCategoryUrl,
  newScoreWidgetUrl,
  backUrl,
  onReorderCategories,
  onReorderCategoryItems,
  onReorderFieldSetFields,
  onNavigate,
  labels,
}: CategoriesManagerProps) {
  const [localCategories, setLocalCategories] = useState(categories);
  const [draggedCategoryId, setDraggedCategoryId] = useState<string | null>(null);

  // Update local state when props change
  React.useEffect(() => {
    setLocalCategories(categories);
  }, [categories]);

  const handleCategoryDragStart = useCallback((e: React.DragEvent, categoryId: string) => {
    setDraggedCategoryId(categoryId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', categoryId);
  }, []);

  const handleCategoryDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleCategoryDrop = useCallback(
    (e: React.DragEvent, targetCategoryId: string) => {
      e.preventDefault();
      if (!draggedCategoryId || draggedCategoryId === targetCategoryId) {
        setDraggedCategoryId(null);
        return;
      }

      const draggedIndex = localCategories.findIndex((c) => c.id === draggedCategoryId);
      const targetIndex = localCategories.findIndex((c) => c.id === targetCategoryId);

      if (draggedIndex === -1 || targetIndex === -1) {
        setDraggedCategoryId(null);
        return;
      }

      const newCategories = [...localCategories];
      const [draggedCategory] = newCategories.splice(draggedIndex, 1);
      newCategories.splice(targetIndex, 0, draggedCategory);

      setLocalCategories(newCategories);
      onReorderCategories(newCategories.map((c) => c.id));
      setDraggedCategoryId(null);
    },
    [draggedCategoryId, localCategories, onReorderCategories]
  );

  const handleCategoryDragEnd = useCallback(() => {
    setDraggedCategoryId(null);
  }, []);

  return (
    <div className="container my-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0 h3">
          <i className="bi bi-layout-three-columns me-2"></i>
          {labels.title}
        </h1>
        <a href={backUrl} className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1"></i>
          <span className="d-none d-sm-inline">{labels.backToDiary}</span>
        </a>
      </div>

      {/* Info Alert */}
      <div className="alert alert-info mb-4 small">
        <i className="bi bi-info-circle me-2"></i>
        {labels.dragDropInfo}
      </div>

      {/* Action Buttons */}
      <div className="mb-4 d-flex flex-wrap gap-2">
        <a href={newCategoryUrl} className="btn btn-primary btn-sm">
          <i className="bi bi-plus-circle me-1"></i>
          {labels.addCategory}
        </a>
        <a href={newScoreWidgetUrl} className="btn btn-outline-primary btn-sm">
          <i className="bi bi-sliders me-1"></i>
          {labels.addScoreWidget}
        </a>
      </div>

      {/* Categories List */}
      <div className="categories-list">
        {localCategories.map((category) => (
          <CategoryCard
            key={category.id}
            category={category}
            isDragging={draggedCategoryId === category.id}
            onDragStart={handleCategoryDragStart}
            onDragOver={handleCategoryDragOver}
            onDrop={handleCategoryDrop}
            onDragEnd={handleCategoryDragEnd}
            onReorderItems={onReorderCategoryItems}
            onReorderFieldSetFields={onReorderFieldSetFields}
            onNavigate={onNavigate}
            labels={labels}
          />
        ))}
      </div>

      {/* Score Widgets */}
      {scoreWidgets.length > 0 && (
        <>
          <h2 className="mt-5 mb-3 h5">
            <i className="bi bi-sliders me-2"></i>
            {labels.scoreWidgets}
          </h2>
          {scoreWidgets.map((widget) => (
            <ScoreWidgetCard key={widget.id} widget={widget} onNavigate={onNavigate} labels={labels} />
          ))}
        </>
      )}
    </div>
  );
}

interface CategoryCardProps {
  category: Category;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent, categoryId: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, categoryId: string) => void;
  onDragEnd: () => void;
  onReorderItems: (categoryId: string, order: string[]) => void;
  onReorderFieldSetFields: (fieldSetId: string, order: string[]) => void;
  onNavigate?: (url: string) => void;
  labels: CategoriesManagerLabels;
}

function CategoryCard({
  category,
  isDragging,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onReorderItems,
  onReorderFieldSetFields,
  onNavigate,
  labels,
}: CategoryCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localItems, setLocalItems] = useState(category.items);
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);

  React.useEffect(() => {
    setLocalItems(category.items);
  }, [category.items]);

  const handleItemDragStart = useCallback((e: React.DragEvent, item: CategoryItem) => {
    e.stopPropagation();
    const compositeId = `${item.itemType}-${item.id}`;
    setDraggedItemId(compositeId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', compositeId);
  }, []);

  const handleItemDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleItemDrop = useCallback(
    (e: React.DragEvent, targetItem: CategoryItem) => {
      e.preventDefault();
      e.stopPropagation();

      const targetCompositeId = `${targetItem.itemType}-${targetItem.id}`;
      if (!draggedItemId || draggedItemId === targetCompositeId) {
        setDraggedItemId(null);
        return;
      }

      const getCompositeId = (i: CategoryItem) => `${i.itemType}-${i.id}`;
      const draggedIndex = localItems.findIndex((i) => getCompositeId(i) === draggedItemId);
      const targetIndex = localItems.findIndex((i) => getCompositeId(i) === targetCompositeId);

      if (draggedIndex === -1 || targetIndex === -1) {
        setDraggedItemId(null);
        return;
      }

      const newItems = [...localItems];
      const [draggedItem] = newItems.splice(draggedIndex, 1);
      newItems.splice(targetIndex, 0, draggedItem);

      setLocalItems(newItems);
      onReorderItems(category.id, newItems.map((i) => i.id));
      setDraggedItemId(null);
    },
    [draggedItemId, localItems, category.id, onReorderItems]
  );

  const handleItemDragEnd = useCallback(() => {
    setDraggedItemId(null);
  }, []);

  return (
    <div
      className={`card shadow-sm mb-3 ${isDragging ? 'opacity-50' : ''}`}
      draggable
      onDragStart={(e) => onDragStart(e, category.id)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, category.id)}
      onDragEnd={onDragEnd}
    >
      <div className="card-header bg-white">
        <div className="d-flex align-items-center">
          <div className="me-2" style={{ cursor: 'move' }}>
            <i className="bi bi-grip-vertical text-muted"></i>
          </div>
          <i
            className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} me-2`}
            style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
            onClick={() => setIsExpanded(!isExpanded)}
          ></i>
          <h6
            className="mb-0 flex-grow-1"
            style={{ cursor: 'pointer' }}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {category.name}
          </h6>
          <div className="d-flex gap-1">
            <a
              href={category.editUrl}
              className="btn btn-sm btn-link text-secondary p-1"
              onClick={(e) => e.stopPropagation()}
            >
              <i className="bi bi-pencil"></i>
            </a>
            <a
              href={category.deleteUrl}
              className="btn btn-sm btn-link text-danger p-1"
              data-turbo-method="delete"
              data-turbo-confirm={labels.confirmDelete}
            >
              <i className="bi bi-trash"></i>
            </a>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="card-body py-2">
          {localItems.length === 0 ? (
            <p className="text-muted text-center mb-0 small">{labels.noActivities}</p>
          ) : (
            localItems.map((item) => (
              <CategoryItemRow
                key={`${item.itemType}-${item.id}`}
                item={item}
                categoryId={category.id}
                isDragging={draggedItemId === `${item.itemType}-${item.id}`}
                onDragStart={handleItemDragStart}
                onDragOver={handleItemDragOver}
                onDrop={handleItemDrop}
                onDragEnd={handleItemDragEnd}
                onReorderFieldSetFields={onReorderFieldSetFields}
                onNavigate={onNavigate}
                labels={labels}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

interface CategoryItemRowProps {
  item: CategoryItem;
  categoryId: string;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent, item: CategoryItem) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, item: CategoryItem) => void;
  onDragEnd: () => void;
  onReorderFieldSetFields: (fieldSetId: string, order: string[]) => void;
  onNavigate?: (url: string) => void;
  labels: CategoriesManagerLabels;
}

function CategoryItemRow({
  item,
  categoryId,
  isDragging,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onReorderFieldSetFields,
  onNavigate,
  labels,
}: CategoryItemRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isFieldSet = item.itemType === 'field_set';

  return (
    <div className={`mb-2 ${isDragging ? 'opacity-50' : ''}`}>
      <div
        className="p-2 bg-light rounded d-flex align-items-center"
        draggable
        onDragStart={(e) => onDragStart(e, item)}
        onDragOver={onDragOver}
        onDrop={(e) => onDrop(e, item)}
        onDragEnd={onDragEnd}
      >
        <i
          className="bi bi-grip-vertical me-2 text-muted"
          style={{ cursor: 'move' }}
        ></i>
        {isFieldSet && (
          <i
            className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} me-2`}
            style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
            onClick={() => setIsExpanded(!isExpanded)}
          ></i>
        )}
        {item.icon && (
          <i
            className={`icon-flex icon-${item.icon} me-2`}
            style={{ width: 20, height: 20 }}
          ></i>
        )}
        <span
          className="flex-grow-1 small"
          style={{ cursor: isFieldSet ? 'pointer' : 'default' }}
          onClick={isFieldSet ? () => setIsExpanded(!isExpanded) : undefined}
        >
          {item.name}
        </span>
        <div className="d-flex gap-1">
          {item.editUrl && (
            <a
              href={item.editUrl}
              className="btn btn-sm btn-link text-secondary p-1"
              onClick={(e) => {
                e.stopPropagation();
                // Use SPA navigation for items with SPA routes
                if (onNavigate) {
                  e.preventDefault();
                  onNavigate(item.editUrl!);
                }
              }}
            >
              <i className="bi bi-pencil"></i>
            </a>
          )}
          {item.deleteUrl && (
            <a
              href={item.deleteUrl}
              className="btn btn-sm btn-link text-danger p-1"
              data-turbo-method="delete"
              data-turbo-confirm={labels.confirmDelete}
            >
              <i className="bi bi-trash"></i>
            </a>
          )}
        </div>
      </div>

      {/* Field set children */}
      {isFieldSet && isExpanded && item.fields && item.fields.length > 0 && (
        <FieldSetFieldsList
          fields={item.fields}
          fieldSetId={item.id}
          onReorderFieldSetFields={onReorderFieldSetFields}
          onNavigate={onNavigate}
          labels={labels}
        />
      )}
    </div>
  );
}

interface FieldSetFieldsListProps {
  fields: CategoryItem[];
  fieldSetId: string;
  onReorderFieldSetFields: (fieldSetId: string, order: string[]) => void;
  onNavigate?: (url: string) => void;
  labels: CategoriesManagerLabels;
}

function FieldSetFieldsList({
  fields,
  fieldSetId,
  onReorderFieldSetFields,
  onNavigate,
  labels,
}: FieldSetFieldsListProps) {
  const [localFields, setLocalFields] = useState(fields);
  const [draggedFieldId, setDraggedFieldId] = useState<string | null>(null);

  React.useEffect(() => {
    setLocalFields(fields);
  }, [fields]);

  const handleFieldDragStart = useCallback((e: React.DragEvent, fieldId: string) => {
    e.stopPropagation();
    setDraggedFieldId(fieldId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', fieldId);
  }, []);

  const handleFieldDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleFieldDrop = useCallback(
    (e: React.DragEvent, targetFieldId: string) => {
      e.preventDefault();
      e.stopPropagation();

      if (!draggedFieldId || draggedFieldId === targetFieldId) {
        setDraggedFieldId(null);
        return;
      }

      const draggedIndex = localFields.findIndex((f) => f.id === draggedFieldId);
      const targetIndex = localFields.findIndex((f) => f.id === targetFieldId);

      if (draggedIndex === -1 || targetIndex === -1) {
        setDraggedFieldId(null);
        return;
      }

      const newFields = [...localFields];
      const [draggedField] = newFields.splice(draggedIndex, 1);
      newFields.splice(targetIndex, 0, draggedField);

      setLocalFields(newFields);
      onReorderFieldSetFields(fieldSetId, newFields.map((f) => f.id));
      setDraggedFieldId(null);
    },
    [draggedFieldId, localFields, fieldSetId, onReorderFieldSetFields]
  );

  const handleFieldDragEnd = useCallback(() => {
    setDraggedFieldId(null);
  }, []);

  return (
    <div className="ms-4 mt-2">
      {localFields.map((field) => (
        <div
          key={`${field.itemType}-${field.id}`}
          className={`mb-2 p-2 bg-white rounded d-flex align-items-center ${
            draggedFieldId === field.id ? 'opacity-50' : ''
          }`}
          style={{ borderLeft: '3px solid #dee2e6' }}
          draggable
          onDragStart={(e) => handleFieldDragStart(e, field.id)}
          onDragOver={handleFieldDragOver}
          onDrop={(e) => handleFieldDrop(e, field.id)}
          onDragEnd={handleFieldDragEnd}
        >
          <i
            className="bi bi-grip-vertical me-2 text-muted"
            style={{ cursor: 'move' }}
          ></i>
          {field.icon && (
            <i
              className={`icon-flex icon-${field.icon} me-2`}
              style={{ width: 20, height: 20 }}
            ></i>
          )}
          <span className="flex-grow-1 small">{field.name}</span>
          <div className="d-flex gap-1">
            {field.editUrl && (
              <a
                href={field.editUrl}
                className="btn btn-sm btn-link text-secondary p-1"
                onClick={(e) => {
                  // Use SPA navigation for items with SPA routes
                  if (onNavigate) {
                    e.preventDefault();
                    onNavigate(field.editUrl!);
                  }
                }}
              >
                <i className="bi bi-pencil"></i>
              </a>
            )}
            {field.deleteUrl && (
              <a
                href={field.deleteUrl}
                className="btn btn-sm btn-link text-danger p-1"
                data-turbo-method="delete"
                data-turbo-confirm={labels.confirmDelete}
              >
                <i className="bi bi-trash"></i>
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

interface ScoreWidgetCardProps {
  widget: ScoreWidget;
  onNavigate?: (url: string) => void;
  labels: CategoriesManagerLabels;
}

function ScoreWidgetCard({ widget, onNavigate, labels }: ScoreWidgetCardProps) {
  return (
    <div className="card shadow-sm mb-3">
      <div className="card-header bg-white">
        <div className="d-flex align-items-center">
          <h6 className="mb-0 flex-grow-1">{widget.name}</h6>
          <div className="d-flex gap-1">
            <a
              href={widget.editUrl}
              className="btn btn-sm btn-link text-secondary p-1"
              onClick={(e) => {
                if (onNavigate) {
                  e.preventDefault();
                  onNavigate(widget.editUrl);
                }
              }}
            >
              <i className="bi bi-pencil"></i>
            </a>
            <a
              href={widget.deleteUrl}
              className="btn btn-sm btn-link text-danger p-1"
              data-turbo-method="delete"
              data-turbo-confirm={labels.confirmDelete}
            >
              <i className="bi bi-trash"></i>
            </a>
          </div>
        </div>
      </div>
      {widget.scoreFields.length > 0 && (
        <div className="card-body py-2">
          <ul className="list-group list-group-flush">
            {widget.scoreFields.map((field) => (
              <li key={field.id} className="list-group-item d-flex align-items-center py-2">
                {field.icon && (
                  <i
                    className={`icon-flex icon-${field.icon} me-2`}
                    style={{ width: 20, height: 20 }}
                  ></i>
                )}
                <span className="small">{field.name}</span>
                <span className="text-muted small ms-2">
                  ({field.minValue} - {field.maxValue})
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default CategoriesManager;
